/**
 * Moon & Jade - Chinese Zodiac Bracelets Shopify Theme
 * JavaScript Modules: Cart, Filters, Products, Navigation and restrained motion
 * @version 2.0.0
 */

(function () {
  'use strict';

  /* ====================================================================
     Utility Helpers
     ==================================================================== */

  /**
   * Debounce function for performance optimization
   * @param {Function} fn - Function to debounce
   * @param {number} delay - Delay in ms (default: 100)
   */
  function debounce(fn, delay = 100) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function preferredScrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /** Format cents using Shopify's configured money format. */
  function formatMoney(cents, format) {
    const config = window.MoonJadeTheme || {};
    const moneyFormat = format || config.moneyFormat;
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';

    const formatNumber = (number, precision = 2, thousands = ',', decimal = '.') => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return precision ? `${whole}${decimal}${parts[1]}` : whole;
    };

    // A Shopify Markets presentment currency can differ from the shop currency.
    // In that case use the currency code Shopify placed on the active cart rather
    // than applying the shop's symbol template to a different currency.
    if (config.currency && config.shopCurrency && config.currency !== config.shopCurrency) {
      return new Intl.NumberFormat(config.locale || document.documentElement.lang || undefined, {
        style: 'currency',
        currency: config.currency
      }).format(value / 100);
    }

    if (moneyFormat && /\{\{\s*\w+\s*\}\}/.test(moneyFormat)) {
      const match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
      let formatted;
      switch (match[1]) {
        case 'amount_no_decimals': formatted = formatNumber(value, 0); break;
        case 'amount_with_comma_separator': formatted = formatNumber(value, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': formatted = formatNumber(value, 0, '.', ','); break;
        case 'amount_with_apostrophe_separator': formatted = formatNumber(value, 2, "'", '.'); break;
        default: formatted = formatNumber(value, 2); break;
      }
      const rendered = moneyFormat.replace(match[0], formatted);
      const decoder = document.createElement('textarea');
      decoder.innerHTML = rendered;
      return typeof decoder.value === 'string' ? decoder.value : rendered;
    }

    if (config.currency) {
      return new Intl.NumberFormat(config.locale || document.documentElement.lang || undefined, {
        style: 'currency',
        currency: config.currency
      }).format(value / 100);
    }

    return formatNumber(value, 2);
  }

  /**
   * Show toast notification
   * @param {string} message - Message to display
   * @param {string} type - 'success' | 'error' | 'info'
   */
  function showToast(message, type = 'success') {
    const toast = document.getElementById('toast');
    if (!toast) return;
    toast.textContent = message;
    toast.style.background = type === 'error' ? 'var(--color-error)' :
      type === 'info' ? 'var(--color-info)' : 'var(--color-dark)';
    toast.classList.add('toast--visible');
    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => toast.classList.remove('toast--visible'), 3000);
  }

  function cartMessage(key, fallback, product) {
    const template = window.MoonJadeTheme?.cartStrings?.[key] || fallback;
    return String(template).replace(/\{\{\s*product\s*\}\}/g, product || '');
  }

  /**
   * Add event delegation helper
   */
  function delegate(parent, eventType, selector, handler) {
    parent.addEventListener(eventType, function (e) {
      const target = e.target.closest(selector);
      if (target && parent.contains(target)) {
        handler.call(target, e);
      }
    });
  }

  /* ====================================================================
     Cart Module
     ==================================================================== */

  const CartModule = {
    cart: null,     // cached cart state from /cart.js
    drawer: null,
    overlay: null,
    _itemsEl: null,
    _footerEl: null,
    _isRendering: false,
    _renderPromise: null,
    _isUpdating: false,  // debounce guard for qty change operations
    _previousFocus: null,

    init() {
      this.drawer = document.getElementById('cart-drawer');
      this.overlay = document.getElementById('cart-overlay');

      // Cache child containers for partial re-render
      this._itemsEl = this.drawer?.querySelector('#cart-drawer-items');
      this._footerEl = this.drawer?.querySelector('#cart-drawer-footer');

      // Cart toggle is handled by the inline onclick="toggleCart()" in header.liquid
      // and the global window.toggleCart bridge. No duplicate addEventListener here.

      // Overlay click closes the cart drawer
      document.getElementById('cart-overlay')?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (event) => {
        if (!this.drawer?.classList.contains('cart-drawer--open')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        } else if (event.key === 'Tab') {
          this._trapFocus(event);
        }
      });

      // Add to cart delegation (product cards)
      delegate(document.body, 'click', '[data-add-to-cart]', function (e) {
        e.preventDefault();
        e.stopPropagation(); // prevent click from bubbling to parent <a> link
        const id = this.dataset.productId;
        const name = this.dataset.productName;
        const price = this.dataset.productPrice;
        // Show loading spinner on the clicked button
        this.classList.add('card__add-btn--loading');
        this.disabled = true;
        CartModule.addItem(id, 1, { name, price }).then(() => {
          CardQuickAdd.closeFrom(this, false);
        }).catch(() => {}).finally(() => {
          this.classList.remove('card__add-btn--loading');
          if (!this.hasAttribute('aria-disabled')) this.disabled = false;
        });
      });

      // Cart drawer internal actions — delegated on the drawer element
      if (this.drawer) {
        // Quantity + / - buttons
        delegate(this.drawer, 'click', '[data-cart-qty]', function (e) {
          e.preventDefault();
          // Prevent rapid double-clicks during update
          if (CartModule._isUpdating) return;
          const line = parseInt(this.dataset.cartQty);
          const delta = parseInt(this.dataset.cartDelta || '0');
          const btn = this;
          btn.classList.add('cart-item__qty-btn--loading');
          btn.disabled = true;
          CartModule._handleQtyChange(line, delta).catch(() => {}).finally(() => {
            btn.classList.remove('cart-item__qty-btn--loading');
            btn.disabled = false;
          });
        });

        // Remove button
        delegate(this.drawer, 'click', '[data-cart-remove]', function (e) {
          e.preventDefault();
          if (CartModule._isUpdating) return;
          const line = parseInt(this.dataset.cartRemove);
          CartModule._handleRemove(line, this.closest('.cart-item')).catch(() => {});
        });
      }
    },

    toggle() {
      if (!this.drawer) return;
      if (this.drawer.classList.contains('cart-drawer--open')) {
        this.close();
      } else {
        this.open();
      }
    },

    open() {
      if (!this.drawer) return;
      this._previousFocus = document.activeElement;
      this.drawer?.classList.add('cart-drawer--open');
      this.drawer?.setAttribute('aria-hidden', 'false');
      this.drawer.inert = false;
      this.overlay?.classList.add('overlay--visible');
      document.getElementById('cart-toggle')?.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => this.drawer?.querySelector('.cart-drawer__close')?.focus());
    },

    close() {
      this.drawer?.classList.remove('cart-drawer--open');
      this.drawer?.setAttribute('aria-hidden', 'true');
      if (this.drawer) this.drawer.inert = true;
      this.overlay?.classList.remove('overlay--visible');
      document.getElementById('cart-toggle')?.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (this._previousFocus && document.contains(this._previousFocus)) this._previousFocus.focus();
      this._previousFocus = null;
    },

    _trapFocus(event) {
      const focusable = Array.from(this.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    /**
     * Add item to cart via Shopify AJAX API.
     * Re-renders the drawer content with fresh cart data, then opens.
     */
    async addItem(variantId, quantity = 1, productInfo = {}) {
      try {
        const themeConfig = window.MoonJadeTheme || {};
        const response = await fetch(themeConfig.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: quantity })
        });
        if (!response.ok) throw new Error(`Cart add returned ${response.status}`);
        await this.render(true);
        if (themeConfig.cartType === 'page') {
          window.location.assign(themeConfig.cartUrl || '/cart');
        } else {
          this.open();
        }
        showToast(`✓ ${cartMessage('itemAdded', '{{ product }} added to cart', productInfo.name)}`);
        // Track event if analytics available
        if (typeof gtag !== 'undefined') {
          gtag('event', 'add_to_cart', {
            items: [{ id: variantId, name: productInfo.name, price: productInfo.price }]
          });
        }
      } catch (error) {
        showToast(cartMessage('addFailed', 'Failed to add item. Please try again.'), 'error');
        throw error;
      }
    },

    /**
     * Update cart item quantity via Shopify AJAX.
     * Re-renders the drawer without page reload.
     */
    async updateItem(lineIndex, quantity) {
      try {
        const themeConfig = window.MoonJadeTheme || {};
        const response = await fetch(themeConfig.cartChangeUrl || '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: lineIndex, quantity: quantity })
        });
        if (!response.ok) throw new Error(`Cart change returned ${response.status}`);
        await this.render(true);
      } catch (error) {
        showToast(cartMessage('updateFailed', 'Failed to update cart.'), 'error');
        throw error;
      }
    },

    /**
     * Remove item from cart by setting quantity to 0.
     * Re-renders the drawer without page reload.
     */
    async removeItem(lineIndex) {
      try {
        const themeConfig = window.MoonJadeTheme || {};
        const response = await fetch(themeConfig.cartChangeUrl || '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: lineIndex, quantity: 0 })
        });
        if (!response.ok) throw new Error(`Cart remove returned ${response.status}`);
        await this.render(true);
      } catch (error) {
        showToast(cartMessage('updateFailed', 'Failed to update cart.'), 'error');
        throw error;
      }
    },

    /**
     * Fetch current cart state and re-render the entire drawer UI.
     * Re-queries #cart-drawer-items / #cart-drawer-footer on every call
     * to guarantee the containers exist (handles late DOM insertion).
     */
    async render(forceFresh = false) {
      if (this._renderPromise) {
        await this._renderPromise;
        if (!forceFresh) return;
      }
      if (this._renderPromise) return this._renderPromise;
      this._isRendering = true;
      this._renderPromise = (async () => {
        try {
        // Show a subtle loading state in the items area
        if (this._itemsEl) {
          this._itemsEl.style.opacity = '0.5';
        }
        const themeConfig = window.MoonJadeTheme || {};
        const sectionUrl = new URL(themeConfig.sectionRenderingUrl || window.location.pathname, window.location.origin);
        sectionUrl.searchParams.set('section_id', 'cart-drawer');
        const [response, sectionResponse] = await Promise.all([
          fetch(themeConfig.cartJsonUrl || '/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }),
          fetch(sectionUrl.toString(), { headers: { Accept: 'text/html' }, cache: 'no-store' })
        ]);
        if (!response.ok) throw new Error(`Cart API returned ${response.status}`);
        if (!sectionResponse.ok) throw new Error(`Cart drawer section returned ${sectionResponse.status}`);
        this.cart = await response.json();
        const sectionHtml = await sectionResponse.text();
        if (this.cart.currency) themeConfig.currency = this.cart.currency;
        // Re-query containers fresh every render — belt-and-suspenders against stale refs
        if (!this.drawer) this.drawer = document.getElementById('cart-drawer');
        this._itemsEl = this.drawer?.querySelector('#cart-drawer-items');
        this._footerEl = this.drawer?.querySelector('#cart-drawer-footer');
        this._updateCountBadge(this.cart.item_count);
        this._renderDrawerSection(sectionHtml);
        } catch (error) {
          console.error('[CartModule] render failed:', error);
          showToast(cartMessage('loadFailed', 'Unable to load cart. Please try again.'), 'error');
          throw error;
        } finally {
          if (this._itemsEl) this._itemsEl.style.opacity = '';
        }
      })();
      try {
        return await this._renderPromise;
      } finally {
        this._renderPromise = null;
        this._isRendering = false;
      }
    },

    /**
     * Update cart count badge in the header.
     */
    _updateCountBadge(count) {
      const countEl = document.getElementById('cart-count');
      if (countEl) {
        countEl.textContent = count;
        countEl.dataset.cartCount = count;
        // Animate badge
        countEl.classList.remove('cart-count--bump');
        void countEl.offsetWidth; // force reflow
        countEl.classList.add('cart-count--bump');
      }
    },

    /** Replace cart contents with Shopify-rendered Liquid.
     * This preserves Markets currency formatting and native accelerated checkout.
     */
    _renderDrawerSection(sectionHtml) {
      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const renderedItems = parsed.querySelector('#cart-drawer-items');
      const renderedFooter = parsed.querySelector('#cart-drawer-footer');
      if (!renderedItems || !renderedFooter || !this._itemsEl || !this._footerEl) {
        throw new Error('Cart drawer section markup is incomplete');
      }
      this._itemsEl.innerHTML = renderedItems.innerHTML;
      this._footerEl.innerHTML = renderedFooter.innerHTML;
    },

    /**
     * Handle quantity +/- button clicks.
     * Reads current quantity from cached cart state, computes new value.
     * If qty reaches 0 → smooth fade + collapse animation, then remove.
     * Otherwise → updateItem and re-render immediately.
     * Uses _isUpdating guard to prevent duplicate concurrent requests.
     */
    async _handleQtyChange(lineIndex, delta) {
      if (this._isUpdating) return;
      this._isUpdating = true;
      let succeeded = false;

      try {
        if (!this.cart) await this.render();
        const item = this.cart?.items?.[lineIndex - 1];
        if (!item) return;

        const currentQty = item.quantity;
        const newQty = currentQty + delta;

        if (newQty <= 0) {
          // Animate item out (fade + collapse), then remove + re-render
          const itemEl = this._itemsEl?.querySelector(`.cart-item[data-line="${lineIndex}"]`);
          await this._animateRemove(itemEl, lineIndex);
          showToast(cartMessage('itemRemoved', 'Removed {{ product }} from cart', item.product_title), 'info');
        } else {
          await this.updateItem(lineIndex, newQty);
        }
        succeeded = true;
      } finally {
        this._isUpdating = false;
        if (succeeded) this._refreshCartPage();
      }
    },

    /**
     * Animate a cart item out: fade + horizontal slide + height collapse.
     * After the animation completes, calls removeItem → render() to rebuild
     * the drawer with correct state. The remaining items reflow naturally
     * because the collapsed item releases its layout space during the animation.
     */
    async _animateRemove(itemEl, lineIndex) {
      if (!itemEl || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        await this.removeItem(lineIndex);
        return;
      }

      // Lock dimensions so the item's layout space stays stable during opacity/slide
      const height = itemEl.offsetHeight;
      itemEl.style.overflow = 'hidden';
      itemEl.style.maxHeight = height + 'px';
      itemEl.style.transition =
        'opacity 0.2s ease, transform 0.2s ease, max-height 0.25s ease 0.15s, margin 0.25s ease 0.15s, padding 0.25s ease 0.15s';

      // Force layout before starting transitions
      itemEl.offsetHeight;

      // Fade + slide out
      itemEl.style.opacity = '0';
      itemEl.style.transform = 'translateX(24px)';

      await new Promise(resolve => setTimeout(resolve, 200));
      {
        itemEl.style.maxHeight = '0px';
        itemEl.style.marginTop = '0px';
        itemEl.style.marginBottom = '0px';
        itemEl.style.paddingTop = '0px';
        itemEl.style.paddingBottom = '0px';
        itemEl.style.borderWidth = '0px';
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      await this.removeItem(lineIndex);
    },

    /**
     * Handle remove button click — same animation, then removal.
     * Shows a toast confirmation after the item is successfully removed.
     */
    async _handleRemove(lineIndex, itemEl) {
      if (this._isUpdating) return;
      this._isUpdating = true;
      let succeeded = false;

      try {
        const item = this.cart?.items?.[lineIndex - 1];
        const productTitle = item?.product_title || 'Item';

        if (!itemEl) {
          await this.removeItem(lineIndex);
        } else {
          await this._animateRemove(itemEl, lineIndex);
        }
        succeeded = true;
        showToast(cartMessage('itemRemoved', 'Removed {{ product }} from cart', productTitle), 'info');
      } finally {
        this._isUpdating = false;
        if (succeeded) this._refreshCartPage();
      }
    },

    async _setQuantity(lineIndex, quantity) {
      if (this._isUpdating) return;
      this._isUpdating = true;
      let succeeded = false;
      try {
        if (quantity <= 0) await this.removeItem(lineIndex);
        else await this.updateItem(lineIndex, quantity);
        succeeded = true;
      } finally {
        this._isUpdating = false;
        if (succeeded) this._refreshCartPage();
      }
    },

    /**
     * Refresh the full cart page if the user is viewing it,
     * otherwise just update the drawer counts are already handled by render().
     */
    _refreshCartPage() {
      const cartPage = document.querySelector('.cart-page');
      if (cartPage) {
        // Reload the page to reflect updated cart state
        window.location.reload();
      }
    }
  };

  /* ====================================================================
     Product Card Quick Add
     ==================================================================== */

  const CardQuickAdd = {
    init() {
      delegate(document.body, 'click', '[data-card-quick-add-toggle]', function (event) {
        event.preventDefault();
        event.stopPropagation();
        CardQuickAdd.toggle(this);
      });

      delegate(document.body, 'click', '[data-card-quick-add-close]', function (event) {
        event.preventDefault();
        CardQuickAdd.close(this.closest('[data-card-quick-add-panel]'), true);
      });

      delegate(document.body, 'change', '[data-card-variant-select]', function () {
        CardQuickAdd.syncVariant(this);
      });

      document.addEventListener('click', event => {
        if (event.target.closest('[data-card-quick-add-panel], [data-card-quick-add-toggle]')) return;
        CardQuickAdd.closeAll();
      });

      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const panel = document.querySelector('[data-card-quick-add-panel]:not([hidden])');
        if (panel) {
          event.preventDefault();
          CardQuickAdd.close(panel, true);
        }
      });

      document.querySelectorAll('[data-card-variant-select]').forEach(select => this.syncVariant(select));
    },

    toggle(toggle) {
      const panel = toggle.closest('.card__media')?.querySelector('[data-card-quick-add-panel]');
      if (!panel) return;
      const shouldOpen = panel.hidden;
      this.closeAll(panel);
      panel.hidden = !shouldOpen;
      toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      if (shouldOpen) {
        window.setTimeout(() => panel.querySelector('[data-card-variant-select]')?.focus(), 20);
      }
    },

    syncVariant(select) {
      const option = select.options[select.selectedIndex];
      const button = select.closest('[data-card-quick-add-panel]')?.querySelector('[data-add-to-cart]');
      if (!option || !button) return;
      button.dataset.productId = option.value;
      button.dataset.productPrice = option.dataset.price || '';
    },

    close(panel, restoreFocus = false) {
      if (!panel || panel.hidden) return;
      panel.hidden = true;
      const toggle = panel.closest('.card__media')?.querySelector('[data-card-quick-add-toggle]');
      toggle?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) toggle?.focus();
    },

    closeFrom(element, restoreFocus = false) {
      this.close(element.closest('[data-card-quick-add-panel]'), restoreFocus);
    },

    closeAll(except = null) {
      document.querySelectorAll('[data-card-quick-add-panel]:not([hidden])').forEach(panel => {
        if (panel !== except) this.close(panel, false);
      });
    }
  };

  /* ====================================================================
     Filter Module
     ==================================================================== */

  const FilterModule = {
    init() {
      const form = document.getElementById('CollectionFiltersForm');
      form?.querySelectorAll('input[type="checkbox"]').forEach(input => {
        input.addEventListener('change', () => form.requestSubmit());
      });

      // Progressive enhancement only. Liquid sets aria-current server-side.
      this._highlightActiveZodiac();
    },

    /**
     * Marks the zodiac link as active if the current URL path matches a zodiac collection.
     */
    _highlightActiveZodiac() {
      const path = window.location.pathname.toLowerCase();
      document.querySelectorAll('.filter-option--link').forEach(link => {
        const href = new URL(link.href, window.location.origin).pathname.toLowerCase();
        if (href && path === href) {
          link.classList.add('filter-option--active');
        }
      });
    },

    applyFilters() {
      // Preserve Shopify's native filter parameters when changing sort order.
      const params = new URLSearchParams(window.location.search);
      const sort = document.getElementById('sort-select')?.value;
      if (sort) params.set('sort_by', sort);
      else params.delete('sort_by');
      params.delete('page');
      const query = params.toString();
      window.location.search = query ? `?${query}` : '';
    }
  };

  /* ====================================================================
     Collection Module (View Toggle & Sort)
     ==================================================================== */

  const CollectionModule = {
    init() {
      // View toggle
      document.querySelectorAll('.view-toggle__btn').forEach(btn => {
        btn.addEventListener('click', () => this.setView(btn));
      });

      // Sort
      document.getElementById('sort-select')?.addEventListener('change', function () {
        FilterModule.applyFilters();
      });
    },

    setView(activeBtn) {
      // Update active button
      document.querySelectorAll('.view-toggle__btn').forEach(b => {
        b.classList.remove('view-toggle__btn--active');
        b.setAttribute('aria-pressed', 'false');
      });
      activeBtn.classList.add('view-toggle__btn--active');
      activeBtn.setAttribute('aria-pressed', 'true');

      const isGrid = activeBtn.dataset.view === 'grid';
      const grid = document.getElementById('product-grid');
      if (!grid) return;

      // Toggle CSS classes — product-card renders are not modified.
      // CSS handles the visual transformation: .product-list-container restyles .card to horizontal layout.
      if (isGrid) {
        grid.classList.remove('product-list-container');
        grid.classList.add('grid', 'grid-4');
      } else {
        grid.classList.add('product-list-container');
        grid.classList.remove('grid', 'grid-4');
      }

      localStorage.setItem('collection-view', isGrid ? 'grid' : 'list');
    },

    /**
     * Initialize saved view preference
     */
    applySavedView() {
      const saved = localStorage.getItem('collection-view');
      if (saved === 'list') {
        const listBtn = document.querySelector('.view-toggle__btn:last-child');
        if (listBtn) this.setView(listBtn);
      }
    }
  };

  /* ====================================================================
     Product Page Module (Variant Selector, Qty, Accordion, Tabs)
     ==================================================================== */

  const ProductModule = {
    root: null,
    form: null,
    variants: [],

    init() {
      this.root = document.querySelector('[data-product-root]');
      this.form = document.getElementById('product-form-main');
      const variantsJson = this.root?.querySelector('[data-product-variants]');
      if (variantsJson) {
        try { this.variants = JSON.parse(variantsJson.textContent); } catch (error) { this.variants = []; }
      }

      // Quantity selector — single event listener using data-qty-delta attribute.
      // Buttons in product form and cart drawer BOTH rely on this delegate.
      delegate(document.body, 'click', '.quantity-selector__btn', function () {
        const input = this.parentElement.querySelector('.quantity-selector__input');
        if (!input) return;
        const deltaAttr = this.getAttribute('data-qty-delta');
        const delta = deltaAttr ? parseInt(deltaAttr) : (this.textContent.trim() === '+' ? 1 : -1);
        const newVal = parseInt(input.value) + delta;
        if (newVal >= 1 && newVal <= 999) {
          input.value = newVal;
          // Dispatch input event so any listeners (e.g. Shopify theme editor) get updated
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });

      // Accordion toggle — handles both tap and click, updates aria-expanded
      delegate(document.body, 'click', '.product-accordion__header', function () {
        const item = this.parentElement;
        const isOpen = item.classList.toggle('product-accordion__item--open');
        this.setAttribute('aria-expanded', isOpen);
        const panel = item.querySelector('.product-accordion__body');
        panel?.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        if (panel) panel.inert = !isOpen;
      });

      delegate(document.body, 'click', '[data-variant-option]', function () {
        ProductModule.selectVariant(this);
      });

      // Tab switching
      delegate(document.body, 'click', '[data-product-tab]', function () {
        const tabName = this.dataset.productTab;
        if (!tabName) return;
        ProductModule.switchTab(tabName, this);
      });
      delegate(document.body, 'keydown', '[data-product-tab]', function (event) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const tabs = Array.from(this.closest('[role="tablist"]')?.querySelectorAll('[data-product-tab]') || []);
        if (!tabs.length) return;
        event.preventDefault();
        const currentIndex = tabs.indexOf(this);
        const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : event.key === 'ArrowRight' ? (currentIndex + 1) % tabs.length : (currentIndex - 1 + tabs.length) % tabs.length;
        ProductModule.switchTab(tabs[nextIndex].dataset.productTab, tabs[nextIndex]);
        tabs[nextIndex].focus();
      });
    },

    switchTab(tabName, btn) {
      document.querySelectorAll('.product-tabs__btn').forEach(b => {
        b.classList.remove('product-tabs__btn--active');
        b.setAttribute('aria-selected', 'false');
        b.setAttribute('tabindex', '-1');
      });
      document.querySelectorAll('.product-tabs__panel').forEach(p => {
        p.classList.remove('product-tabs__panel--active');
        p.hidden = true;
      });

      if (btn) {
        btn.classList.add('product-tabs__btn--active');
        btn.setAttribute('aria-selected', 'true');
        btn.setAttribute('tabindex', '0');
      }
      const panel = document.getElementById(`tab-${tabName}`);
      if (panel) {
        panel.classList.add('product-tabs__panel--active');
        panel.hidden = false;
      }
    },

    /**
     * Select variant option on PDP
     */
    selectVariant(button) {
      if (!this.root || !button) return;
      const group = button.closest('.product-variants');
      if (!group) return;
      group.querySelectorAll('[data-variant-option]').forEach(option => {
        const selected = option === button;
        option.classList.toggle('variant-option--selected', selected);
        option.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      const selectedLabel = group.querySelector('[data-selected-option]');
      if (selectedLabel) selectedLabel.textContent = button.dataset.optionValue;
      this.updateVariant();
    },

    updateVariant() {
      if (!this.root || !this.form || !this.variants.length) return;
      const selectedOptions = Array.from(this.root.querySelectorAll('.product-variants'))
        .map(group => group.querySelector('[data-variant-option][aria-pressed="true"]')?.dataset.optionValue || '');
      this.applyVariant(this.findVariant(selectedOptions));
    },

    findVariant(selectedOptions) {
      return this.variants.find(item => item.options.every((option, index) => option === selectedOptions[index])) || null;
    },

    applyVariant(variant) {
      const idInput = this.form?.querySelector('[data-variant-id]');
      const priceEl = this.root.querySelector('[data-product-price]');
      const compareEl = this.root.querySelector('[data-product-compare-price]');
      const savingEl = this.root.querySelector('[data-product-saving]');
      const unitPriceEl = this.root.querySelector('[data-unit-price]');
      const availabilityEl = this.root.querySelector('[data-product-availability]');
      const addButton = this.root.querySelector('[data-product-add]');
      const acceleratedCheckout = this.root.querySelector('[data-product-accelerated-checkout]');
      const addText = addButton?.querySelector('.btn-add-to-cart__text');
      const available = Boolean(variant && variant.available);

      if (idInput) {
        idInput.value = variant ? variant.id : '';
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (priceEl) priceEl.textContent = variant ? formatMoney(variant.price) : '';

      const hasComparePrice = Boolean(variant && variant.compare_at_price && variant.compare_at_price > variant.price);
      if (compareEl) {
        compareEl.hidden = !hasComparePrice;
        compareEl.textContent = hasComparePrice ? formatMoney(variant.compare_at_price) : '';
      }
      if (savingEl) {
        savingEl.hidden = !hasComparePrice;
        savingEl.textContent = hasComparePrice ? `${this.root.dataset.saveLabel} ${formatMoney(variant.compare_at_price - variant.price)}` : '';
      }
      if (unitPriceEl) {
        const measurement = variant?.unit_price_measurement;
        const hasUnitPrice = Boolean(variant?.unit_price && measurement);
        unitPriceEl.hidden = !hasUnitPrice;
        unitPriceEl.textContent = hasUnitPrice
          ? `${formatMoney(variant.unit_price)} / ${measurement.reference_value !== 1 ? measurement.reference_value : ''}${measurement.reference_unit}`
          : '';
      }

      if (addButton) {
        addButton.disabled = !available;
        addButton.setAttribute('aria-disabled', available ? 'false' : 'true');
      }
      if (acceleratedCheckout) acceleratedCheckout.hidden = !available;
      if (addText) addText.textContent = available ? this.root.dataset.addLabel : this.root.dataset.soldOutLabel;
      if (availabilityEl) availabilityEl.textContent = available ? this.root.dataset.inStockLabel : (variant ? this.root.dataset.soldOutLabel : this.root.dataset.unavailableLabel);

      if (variant && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
  };

  /* ====================================================================
     Global Functions (exposed for inline onclick handlers)
     ==================================================================== */

  window.toggleCart = () => CartModule.toggle();
  let mobileMenuPreviousFocus = null;
  window.toggleMobileMenu = (forceOpen) => {
    const drawer = document.getElementById('mobile-drawer');
    const backdrop = document.getElementById('mobile-menu-overlay-backdrop');
    const toggle = document.getElementById('mobile-menu-toggle');
    if (!drawer) return;
    const shouldOpen = typeof forceOpen === 'boolean' ? forceOpen : !drawer.classList.contains('mobile-drawer--open');
    drawer.classList.toggle('mobile-drawer--open', shouldOpen);
    drawer.setAttribute('aria-hidden', shouldOpen ? 'false' : 'true');
    drawer.inert = !shouldOpen;
    backdrop?.classList.toggle('mobile-drawer__backdrop--visible', shouldOpen);
    toggle?.classList.toggle('header__menu-toggle--active', shouldOpen);
    toggle?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    document.body.style.overflow = shouldOpen ? 'hidden' : '';
    if (shouldOpen) {
      mobileMenuPreviousFocus = document.activeElement;
      requestAnimationFrame(() => drawer.querySelector('button, a[href]')?.focus());
    } else if (mobileMenuPreviousFocus && document.contains(mobileMenuPreviousFocus)) {
      mobileMenuPreviousFocus.focus();
      mobileMenuPreviousFocus = null;
    }
  };

  document.getElementById('mobile-menu-overlay-backdrop')?.addEventListener('click', () => window.toggleMobileMenu(false));
  window.addEventListener('resize', debounce(() => {
    if (window.innerWidth > 1199 && document.getElementById('mobile-drawer')?.classList.contains('mobile-drawer--open')) {
      window.toggleMobileMenu(false);
    }
  }, 100));

  document.addEventListener('keydown', event => {
    const drawer = document.getElementById('mobile-drawer');
    if (!drawer?.classList.contains('mobile-drawer--open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      window.toggleMobileMenu(false);
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  });

  /**
   * Add to cart from product detail page — AJAX-based, no page reload.
   * Reads the product variant ID from the hidden form input and quantity from the selector.
   * Shows loading spinner and success feedback instantly.
   */
  window.addToCartFromProduct = async () => {
    const form = document.querySelector('form[action*="/cart/add"]');
    const btn = document.querySelector('.btn-add-to-cart');
    if (!form || !btn) return;

    const variantInput = form.querySelector('input[name="id"]');
    const qtyInput = document.getElementById('product-qty');
    if (!variantInput) return;

    const variantId = variantInput.value;
    const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;
    const productName = document.querySelector('.product-info__title')?.textContent?.trim() || 'Item';
    const priceText = document.querySelector('.product-info__current-price')?.textContent?.trim() || '';

    // Show loading state
    const textEl = btn.querySelector('.btn-add-to-cart__text');
    const spinnerEl = btn.querySelector('.btn-add-to-cart__spinner');
    btn.classList.add('btn--loading');
    btn.disabled = true;
    if (textEl) textEl.style.visibility = 'hidden';
    if (spinnerEl) spinnerEl.style.display = '';

    try {
      await CartModule.addItem(variantId, quantity, { name: productName, price: priceText });
    } catch (err) {
      // CartModule displays the user-facing error.
    } finally {
      // Restore button state
      btn.classList.remove('btn--loading');
      btn.disabled = btn.getAttribute('aria-disabled') === 'true';
      if (textEl) textEl.style.visibility = '';
      if (spinnerEl) spinnerEl.style.display = 'none';
    }
  };

  window.updateQty = (delta) => {
    const input = document.getElementById('product-qty');
    if (input) {
      const newVal = parseInt(input.value) + delta;
      if (newVal >= 1 && newVal <= 999) input.value = newVal;
    }
  };

  window.toggleAccordion = (headerEl) => {
    const item = headerEl.parentElement;
    const isOpen = item.classList.toggle('product-accordion__item--open');
    headerEl.setAttribute('aria-expanded', isOpen);
    const panel = item.querySelector('.product-accordion__body');
    panel?.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    if (panel) panel.inert = !isOpen;
  };

  /**
   * Product Gallery Module
   * Handles horizontal scroll-snap gallery with dot indicators and thumbnail navigation.
   * Uses native CSS scroll-snap for momentum scrolling and edge bounce on mobile.
   */
  const ProductGallery = {
    scrollContainer: null,
    slides: null,
    thumbs: null,
    dots: null,
    currentIndex: 0,
    _scrollTimeout: null,

    /**
     * Initialize the product gallery
     */
    init() {
      this.scrollContainer = document.getElementById('product-gallery-scroll');
      if (!this.scrollContainer) return;

      this.slides = this.scrollContainer.querySelectorAll('.product-gallery__slide');
      this.thumbs = document.querySelectorAll('#product-gallery-thumbs .product-gallery__thumb');
      this.dots = document.querySelectorAll('#product-gallery-dots .product-gallery__dot');

      if (!this.slides.length) return;

      // Thumbnail click → scroll to slide
      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.goToSlide(i));
      });

      // Dot click → scroll to slide
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.goToSlide(i));
      });

      // Arrow buttons (desktop)
      var prevArrow = document.getElementById('gallery-arrow-prev');
      var nextArrow = document.getElementById('gallery-arrow-next');
      if (prevArrow) prevArrow.addEventListener('click', () => this.goToSlide(Math.max(0, this.currentIndex - 1)));
      if (nextArrow) nextArrow.addEventListener('click', () => this.goToSlide(Math.min(this.slides.length - 1, this.currentIndex + 1)));

      // Scroll listener → update active dot/thumb
      this.scrollContainer.addEventListener('scroll', () => this._onScroll());

      // Keyboard navigation (left/right arrows when gallery is focused)
      this.scrollContainer.setAttribute('tabindex', '0');
      this.scrollContainer.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.goToSlide(Math.max(0, this.currentIndex - 1));
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.goToSlide(Math.min(this.slides.length - 1, this.currentIndex + 1));
        }
      });
    },

    /**
     * Scroll to a specific slide by index
     * @param {number} index - Zero-based slide index
     */
    goToSlide(index) {
      if (!this.scrollContainer || !this.slides[index]) return;
      const slide = this.slides[index];
      this.scrollContainer.scrollTo({
        left: slide.offsetLeft,
        behavior: preferredScrollBehavior()
      });
    },

    /**
     * Handle scroll event — debounce and update active state
     */
    _onScroll() {
      if (this._scrollTimeout) {
        cancelAnimationFrame(this._scrollTimeout);
      }
      // Use rAF for performant scroll handling
      this._scrollTimeout = requestAnimationFrame(() => {
        this._updateActiveIndex();
      });
    },

    /**
     * Determine which slide is currently most visible and update UI
     */
    _updateActiveIndex() {
      if (!this.scrollContainer || !this.slides.length) return;

      const scrollLeft = this.scrollContainer.scrollLeft;
      const slideWidth = this.slides[0].offsetWidth;
      if (!slideWidth) return;

      // Calculate closest slide index
      const index = Math.round(scrollLeft / slideWidth);
      const clampedIndex = Math.max(0, Math.min(this.slides.length - 1, index));

      if (clampedIndex !== this.currentIndex) {
        this.currentIndex = clampedIndex;
        this._updateIndicators(clampedIndex);
      }
    },

    /**
     * Update active state of dot and thumbnail indicators
     * @param {number} index - Currently active slide index
     */
    _updateIndicators(index) {
      // Update dots
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('product-gallery__dot--active', i === index);
        dot.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      });

      // Update thumbnails
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('product-gallery__thumb--active', i === index);
        thumb.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      });

      // Update arrow visibility at edges
      var prevArrow = document.getElementById('gallery-arrow-prev');
      var nextArrow = document.getElementById('gallery-arrow-next');
      if (prevArrow) {
        prevArrow.style.visibility = (index <= 0) ? 'hidden' : '';
      }
      if (nextArrow) {
        nextArrow.style.visibility = (index >= this.slides.length - 1) ? 'hidden' : '';
      }
    }
  };

  // Backward compatibility — keep old function working
  window.switchProductImage = (index) => {
    if (typeof ProductGallery !== 'undefined' && ProductGallery.scrollContainer) {
      ProductGallery.goToSlide(index);
    }
  };

  window.switchTab = (tabName) => {
    const btn = document.querySelector(`[data-product-tab="${tabName}"]`);
    ProductModule.switchTab(tabName, btn);
  };

  /**
   * Cart drawer & cart page quantity +/- handler.
   * Computes new absolute quantity from delta, handles 0 → removal.
   * For cart page buttons: disables all qty inputs during update, reloads after completion.
   */
  window.updateCartItem = (lineIndex, delta) => {
    if (CartModule._isUpdating) return;
    // Disable all qty-related controls on the cart page during update
    const cartPage = document.querySelector('.cart-page');
    if (cartPage) {
      cartPage.querySelectorAll('.cart-item__qty-btn, .cart-item__qty-input, .cart-item__remove')
        .forEach(el => { el.disabled = true; el.style.pointerEvents = 'none'; el.style.opacity = '0.4'; });
    }
    CartModule._handleQtyChange(lineIndex, delta).catch(() => {}).finally(() => {
      cartPage?.querySelectorAll('.cart-item__qty-btn, .cart-item__qty-input, .cart-item__remove')
        .forEach(el => { el.disabled = false; el.style.pointerEvents = ''; el.style.opacity = ''; });
    });
  };

  /**
   * Cart page remove button handler.
   * Animates item out, disables controls during removal, reloads page after completion.
   */
  window.removeCartItem = (lineIndex) => {
    if (CartModule._isUpdating) return;
    const cartPage = document.querySelector('.cart-page');
    const itemEl = cartPage?.querySelector(`.cart-item[data-line="${lineIndex}"]`);
    if (cartPage) {
      cartPage.querySelectorAll('.cart-item__qty-btn, .cart-item__qty-input, .cart-item__remove')
        .forEach(el => { el.disabled = true; el.style.pointerEvents = 'none'; el.style.opacity = '0.4'; });
    }
    CartModule._handleRemove(lineIndex, itemEl).catch(() => {}).finally(() => {
      cartPage?.querySelectorAll('.cart-item__qty-btn, .cart-item__qty-input, .cart-item__remove')
        .forEach(el => { el.disabled = false; el.style.pointerEvents = ''; el.style.opacity = ''; });
    });
  };

  /**
   * Cart drawer direct quantity input (used by initial Liquid-rendered input).
   */
  window.updateCartItemQty = (input) => {
    const lineIndex = parseInt(input.dataset.line);
    const qty = parseInt(input.value);
    if (!Number.isInteger(lineIndex) || !Number.isInteger(qty)) return;
    CartModule._setQuantity(lineIndex, qty).catch(() => {});
  };

  window.setView = (viewType) => {
    // Find the matching button
    const btns = document.querySelectorAll('.view-toggle__btn');
    let btn;
    if (viewType === 'grid') btn = btns[0];
    else if (viewType === 'list') btn = btns[1];
    if (btn) CollectionModule.setView(btn);
  };

  window.applySort = () => {
    FilterModule.applyFilters();
  };

  let filterSidebarReturnFocus = null;
  const syncFilterSidebarMode = () => {
    const sidebar = document.getElementById('filter-sidebar');
    if (!sidebar) return;
    const isDrawer = window.matchMedia('(max-width: 1023px)').matches;
    const isOpen = sidebar.classList.contains('filter-sidebar--open');
    if (!isDrawer) {
      sidebar.classList.remove('filter-sidebar--open');
      sidebar.removeAttribute('role');
      sidebar.removeAttribute('aria-modal');
      sidebar.setAttribute('aria-hidden', 'false');
      sidebar.inert = false;
      document.getElementById('filter-overlay')?.classList.remove('filter-overlay--visible');
      document.getElementById('filter-toggle')?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('filter-open');
      return;
    }
    sidebar.setAttribute('role', 'dialog');
    sidebar.setAttribute('aria-modal', 'true');
    sidebar.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    sidebar.inert = !isOpen;
  };

  window.toggleFilterSidebar = (force) => {
    const sidebar = document.getElementById('filter-sidebar');
    const overlay = document.getElementById('filter-overlay');
    if (!sidebar) return;
    const shouldOpen = typeof force === 'boolean'
      ? force
      : !sidebar.classList.contains('filter-sidebar--open');
    sidebar.classList.toggle('filter-sidebar--open', shouldOpen);
    const isOpen = shouldOpen;
    sidebar.setAttribute('role', 'dialog');
    sidebar.setAttribute('aria-modal', 'true');
    sidebar.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    sidebar.inert = !isOpen;
    document.getElementById('filter-toggle')?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (overlay) {
      overlay.classList.toggle('filter-overlay--visible', isOpen);
      overlay.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    }

    if (isOpen) {
      filterSidebarReturnFocus = document.activeElement;
      document.body.classList.add('filter-open');
      window.setTimeout(() => sidebar.querySelector('.filter-sidebar__close, a, button, input')?.focus(), 50);
    } else {
      document.body.classList.remove('filter-open');
      if (filterSidebarReturnFocus instanceof HTMLElement) filterSidebarReturnFocus.focus();
      filterSidebarReturnFocus = null;
    }
  };

  window.addEventListener('resize', debounce(syncFilterSidebarMode, 100), { passive: true });

  document.addEventListener('keydown', event => {
    const sidebar = document.getElementById('filter-sidebar');
    if (!sidebar?.classList.contains('filter-sidebar--open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      window.toggleFilterSidebar(false);
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(sidebar.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  window.clearAllFilters = () => {
    const form = document.getElementById('CollectionFiltersForm');
    if (form) window.location.assign(form.action);
  };

  window.updateCartNote = (note) => {
    const themeConfig = window.MoonJadeTheme || {};
    fetch(themeConfig.cartUpdateUrl || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note })
    }).catch(() => {});
  };

  /* ====================================================================
     Back to Top Button
     ==================================================================== */

  const BackToTop = {
    init() {
      const btn = document.getElementById('back-to-top');
      if (!btn) return;
      window.addEventListener('scroll', debounce(() => {
        btn.classList.toggle('back-to-top--visible', window.scrollY > 400);
      }, 100));
      btn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: preferredScrollBehavior() });
      });
    }
  };

  /* ====================================================================
     Header Scroll Effect
     ==================================================================== */

  /* ====================================================================
     Smart Header — keep navigation predictable and optionally collapse
     the announcement bar after the visitor begins scrolling.
     ==================================================================== */

  const SmartHeader = {
    ticking: false,
    topBar: null,
    mainContent: null,

    init() {
      this.topBar = document.getElementById('top-bar');
      this.mainContent = document.getElementById('main-content');
      if (!this.topBar || !this.mainContent) return;

      this._setPadding();
      this._updateState();

      if (this.topBar.dataset.sticky !== 'false') {
        window.addEventListener('scroll', () => this._scheduleUpdate(), { passive: true });
      }
      window.addEventListener('resize', debounce(() => this._setPadding(), 100), { passive: true });
    },

    _scheduleUpdate() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this._updateState();
        this.ticking = false;
      });
    },

    _updateState() {
      if (!this.topBar || this.topBar.dataset.sticky === 'false') return;
      this.topBar.classList.toggle('top-bar--scrolled', window.scrollY > 20);
      this.topBar.classList.remove('top-bar--hidden');
    },

    _setPadding() {
      if (!this.topBar || !this.mainContent) return;
      this.mainContent.style.paddingTop = this.topBar.dataset.sticky === 'false'
        ? '0px'
        : this.topBar.offsetHeight + 'px';
    }
  };

  /* ====================================================================
     Animation on Scroll (AOS)
     ==================================================================== */

  const AnimOnScroll = {
    init() {
      const revealElements = document.querySelectorAll('.scroll-reveal');

      if (!('IntersectionObserver' in window)) {
        revealElements.forEach(el => {
          el.classList.add('scroll-reveal--visible');
        });
        return;
      }

      // Skip animations for users who prefer reduced motion
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        revealElements.forEach(el => {
          el.classList.add('scroll-reveal--visible');
        });
        return;
      }

      // Opt-in child sequencing is enabled only after the reveal observer is
      // available. Without JavaScript, mobile promo copy remains visible.
      document.querySelectorAll('[data-reveal-sequence="promo-media-text"]').forEach(el => {
        el.classList.add('scroll-reveal--ready');
      });

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Fade-up elements outside the hero use the shared observer.
            if (entry.target.classList.contains('animate-fade-up')) {
              entry.target.style.opacity = '1';
              entry.target.style.transform = 'translateY(0)';
              entry.target.style.willChange = 'auto';
              observer.unobserve(entry.target);
            }
            // Section scroll-reveal blocks
            if (entry.target.classList.contains('scroll-reveal')) {
              entry.target.classList.add('scroll-reveal--visible');
              observer.unobserve(entry.target);
            }
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

      // Hero elements use their lightweight CSS entrance animation.
      document.querySelectorAll('.animate-fade-up').forEach(el => {
        if (el.closest('.hero__content')) return;
        el.style.opacity = '0';
        el.style.transform = 'translateY(24px)';
        el.style.willChange = 'opacity, transform';
        observer.observe(el);
      });

      // Observe scroll-reveal sections
      revealElements.forEach(el => {
        observer.observe(el);
      });
    }
  };

  /* ====================================================================
     Homepage hero presentation from the original Moon & Jade UI.
     This is isolated from product, cart and checkout behavior.
     ==================================================================== */

  const HeroPresentation = {
    ticking: false,
    hero: null,
    spacer: null,
    image: null,
    overlay: null,
    content: null,
    lastProgress: -1,

    init() {
      this.hero = document.querySelector('.hero');
      this.spacer = document.querySelector('.hero__spacer');
      if (!this.hero || !this.spacer || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.image = this.hero.querySelector('.hero__bg');
      this.overlay = this.hero.querySelector('.hero__overlay');
      this.content = this.hero.querySelector('.hero__content');
      if (!this.image || !this.overlay || !this.content) return;

      this.hero.classList.add('hero--sticky', 'hero--parallax');
      this._setSpacerHeight();
      Array.from(this.content.children).forEach(child => {
        child.style.opacity = '1';
        child.style.transform = 'translateY(0)';
      });

      window.addEventListener('scroll', () => this._scheduleUpdate(), { passive: true });
      window.addEventListener('resize', debounce(() => {
        this._setSpacerHeight();
        this._scheduleUpdate();
      }, 100), { passive: true });
      this._update();
    },

    _setSpacerHeight() {
      this.spacer.style.height = Math.round(window.innerHeight) + 'px';
    },

    _scheduleUpdate() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this._update();
        this.ticking = false;
      });
    },

    _update() {
      const spacerRect = this.spacer.getBoundingClientRect();
      const spacerHeight = this.spacer.offsetHeight;
      if (!spacerHeight) return;

      const progress = Math.max(0, Math.min(1, (window.innerHeight - spacerRect.top) / spacerHeight));
      if (Math.abs(progress - this.lastProgress) < 0.001) return;
      this.lastProgress = progress;

      this.image.style.setProperty('--hero-zoom', (1 + progress * 0.25).toFixed(4));
      this.overlay.style.setProperty('--hero-overlay', (progress * 0.35).toFixed(3));

      const textProgress = Math.min(progress / 0.8, 1);
      Array.from(this.content.children).forEach(child => {
        child.style.opacity = (1 - textProgress).toFixed(3);
        child.style.transform = `translateY(${(textProgress * 28).toFixed(1)}px)`;
      });
    }
  };

  /* ====================================================================
     Collection Lineup — horizontally scrollable homepage collection cards
     ==================================================================== */

  const CollectionLineup = {
    init() {
      const lineup = document.getElementById('collection-lineup');
      if (!lineup) return;

      const viewport = document.getElementById('collection-lineup-viewport');
      const track = document.getElementById('collection-lineup-track');
      const prevBtn = lineup.querySelector('.collection-lineup__nav-btn--prev');
      const nextBtn = lineup.querySelector('.collection-lineup__nav-btn--next');
      const cards = track ? track.querySelectorAll('.collection-lineup__card') : [];
      if (!viewport || !track || cards.length === 0) return;

      function getScrollAmount() {
        const cardEl = cards[0];
        if (!cardEl) return 260;
        const style = window.getComputedStyle(track);
        const gap = parseInt(style.columnGap || style.gap || 0);
        return cardEl.offsetWidth + (isNaN(gap) ? 0 : gap);
      }

      function updateArrows() {
        if (!prevBtn || !nextBtn) return;
        const atStart = viewport.scrollLeft <= 2;
        const atEnd = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 2;
        prevBtn.disabled = atStart;
        nextBtn.disabled = atEnd;
        prevBtn.setAttribute('aria-disabled', String(atStart));
        nextBtn.setAttribute('aria-disabled', String(atEnd));
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          viewport.scrollBy({ left: -getScrollAmount(), behavior: preferredScrollBehavior() });
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          viewport.scrollBy({ left: getScrollAmount(), behavior: preferredScrollBehavior() });
        });
      }

      viewport.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', debounce(updateArrows, 100), { passive: true });
      updateArrows();
    }
  };

  /* ====================================================================
     Zodiac Carousel — finite, scroll-snapped list with no cloned DOM.
     ==================================================================== */

  const ZodiacCarousel = {
    init() {
      const carousel = document.getElementById('zodiac-carousel');
      if (!carousel) return;

      const viewport = document.getElementById('zodiac-carousel-viewport');
      const track = document.getElementById('zodiac-carousel-track');
      const prevBtn = carousel.querySelector('.zodiac-carousel__nav-btn--prev');
      const nextBtn = carousel.querySelector('.zodiac-carousel__nav-btn--next');
      if (!viewport || !track) return;

      const cards = track.querySelectorAll('.zodiac-carousel__card');
      if (cards.length === 0) return;

      function getScrollAmount() {
        const card = cards[0];
        if (!card) return 260;
        const style = window.getComputedStyle(track);
        const gap = parseFloat(style.columnGap || style.gap || 0) || 0;
        return card.offsetWidth + gap;
      }

      function updateArrows() {
        if (!prevBtn || !nextBtn) return;
        const atStart = viewport.scrollLeft <= 2;
        const atEnd = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 2;
        prevBtn.disabled = atStart;
        nextBtn.disabled = atEnd;
        prevBtn.setAttribute('aria-disabled', String(atStart));
        nextBtn.setAttribute('aria-disabled', String(atEnd));
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          viewport.scrollBy({ left: -getScrollAmount(), behavior: preferredScrollBehavior() });
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          viewport.scrollBy({ left: getScrollAmount(), behavior: preferredScrollBehavior() });
        });
      }

      viewport.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', debounce(updateArrows, 100), { passive: true });
      updateArrows();
    }
  };

  /* ====================================================================
     Zodiac Donut Module — interactive donut chart with info panel
     ==================================================================== */

  const ZodiacDonut = {
    segments: null,
    infoDefault: null,
    infoDetails: null,
    activeIndex: -1,

    init() {
      this.segments = document.querySelectorAll('.zodiac-seg');
      if (!this.segments.length) return;

      this.infoDefault = document.getElementById('zodiac-info-default');
      this.infoDetails = [];
      for (let i = 0; i < 12; i++) {
        this.infoDetails[i] = document.getElementById('zodiac-info-' + i);
      }

      this.segments.forEach(seg => {
        seg.setAttribute('aria-pressed', 'false');
        seg.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._toggleDetail(parseInt(seg.dataset.index));
        });
        seg.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          e.stopPropagation();
          this._toggleDetail(parseInt(seg.dataset.index));
        });
      });

      // Click outside to deselect
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.zodiac-seg') && !e.target.closest('.zodiac-info')) {
          this._hideDetail();
        }
      });
    },

    _toggleDetail(index) {
      if (this.activeIndex === index) {
        this._hideDetail();
        return;
      }
      this._showDetail(index);
    },

    _showDetail(index) {
      // Show the selected detail panel, hide all others + default panel
      var prevIndex = this.activeIndex;
      this.activeIndex = index;

      // Hide default panel
      if (this.infoDefault) {
        this.infoDefault.classList.add('is-hidden');
      }

      // Deactivate previous detail
      if (prevIndex !== -1) {
        var prev = this.infoDetails[prevIndex];
        if (prev) prev.classList.remove('is-active');
      }

      // Activate new detail
      var newPanel = this.infoDetails[index];
      if (newPanel) newPanel.classList.add('is-active');

      // Update segment highlights
      this.segments.forEach(function(s) {
        s.classList.remove('zodiac-seg--active');
        s.setAttribute('aria-pressed', 'false');
      });
      var activeSeg = this.segments[index];
      if (activeSeg) {
        activeSeg.classList.add('zodiac-seg--active');
        activeSeg.setAttribute('aria-pressed', 'true');
      }
    },

    _hideDetail() {
      if (this.activeIndex === -1) return;

      var prevIndex = this.activeIndex;
      this.activeIndex = -1;

      // Hide current detail
      var prev = this.infoDetails[prevIndex];
      if (prev) prev.classList.remove('is-active');

      // Show default panel
      if (this.infoDefault) {
        this.infoDefault.classList.remove('is-hidden');
      }

      // Clear segment highlights
      this.segments.forEach(function(s) {
        s.classList.remove('zodiac-seg--active');
        s.setAttribute('aria-pressed', 'false');
      });
    }
  };

  /* ====================================================================
     Shared Help / Legal accordion
     ==================================================================== */

  const AccessibleAccordion = {
    reduceMotion: false,

    init() {
      const buttons = Array.from(document.querySelectorAll('[data-accordion-button]'));
      if (!buttons.length) return;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      buttons.forEach(button => {
        const panelId = button.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (!panel) return;

        button.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');
        panel.inert = true;
        panel.hidden = true;
        panel.style.maxHeight = '0px';
        panel.style.opacity = '0';

        button.addEventListener('click', () => this.toggle(button, panel));
        panel.addEventListener('transitionend', event => {
          if (event.propertyName !== 'max-height') return;
          if (button.getAttribute('aria-expanded') === 'false') {
            panel.hidden = true;
          } else {
            panel.style.maxHeight = 'none';
          }
        });
      });

      window.addEventListener('resize', debounce(() => {
        document.querySelectorAll('[data-accordion-button][aria-expanded="true"]').forEach(button => {
          const panel = document.getElementById(button.getAttribute('aria-controls'));
          if (panel) panel.style.maxHeight = 'none';
        });
      }, 100), { passive: true });
    },

    toggle(button, panel) {
      const item = button.closest('[data-accordion-item]');
      const willOpen = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(willOpen));
      panel.setAttribute('aria-hidden', String(!willOpen));
      panel.inert = !willOpen;
      item?.classList.toggle('mj-accordion__item--open', willOpen);

      if (willOpen) {
        panel.hidden = false;
        panel.style.opacity = '1';
        if (this.reduceMotion) {
          panel.style.maxHeight = 'none';
          return;
        }
        panel.style.maxHeight = '0px';
        // Read after display is restored, then animate to the measured height.
        panel.getBoundingClientRect();
        requestAnimationFrame(() => {
          panel.style.maxHeight = `${panel.scrollHeight}px`;
        });
        return;
      }

      if (this.reduceMotion) {
        panel.style.maxHeight = '0px';
        panel.style.opacity = '0';
        panel.hidden = true;
        return;
      }
      panel.style.maxHeight = `${panel.scrollHeight}px`;
      panel.getBoundingClientRect();
      requestAnimationFrame(() => {
        panel.style.maxHeight = '0px';
        panel.style.opacity = '0';
      });
    }
  };

  /* Native Shopify owns contact submission. This only exposes a truthful
     in-progress state after browser validation succeeds. */
  const ContactFormPresentation = {
    init() {
      document.querySelectorAll('#ContactForm, #FeedbackForm').forEach(form => {
        if (form.dataset.nativeFormInitialized === 'true') return;
        form.dataset.nativeFormInitialized = 'true';

        const message = form.querySelector('[data-feedback-message]');
        const counter = form.querySelector('[data-feedback-count]');
        const updateCounter = () => {
          if (message && counter) counter.textContent = `${message.value.length} / 4000`;
        };
        message?.addEventListener('input', updateCounter);
        updateCounter();

        form.addEventListener('submit', () => {
          if (!form.checkValidity()) return;
          const submit = form.querySelector('[data-contact-submit]');
          if (!submit) return;
          submit.setAttribute('aria-busy', 'true');
          submit.textContent = submit.dataset.loadingLabel || 'Sending…';
        });
      });
    }
  };

  const NewsletterFormPresentation = {
    init() {
      document.querySelectorAll('.newsletter__form').forEach(form => {
        form.addEventListener('submit', () => {
          if (!form.checkValidity()) return;
          const submit = form.querySelector('[data-newsletter-submit]');
          if (!submit) return;
          submit.setAttribute('aria-busy', 'true');
          submit.textContent = submit.dataset.loadingLabel || 'Subscribing…';
        });
      });
    }
  };

  /* Shopify localization forms own locale, country, market and currency state.
     This module only submits the native form and manages the details UI. */
  const LocalizationForms = {
    init() {
      const selectors = Array.from(document.querySelectorAll('[data-localization-selector]'));
      if (!selectors.length) return;

      document.querySelectorAll('[data-localization-select]').forEach(select => {
        select.addEventListener('change', () => {
          const form = select.closest('form');
          if (!form) return;
          select.setAttribute('aria-busy', 'true');
          form.setAttribute('aria-busy', 'true');
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.submit();
        });
      });

      document.addEventListener('click', event => {
        selectors.forEach(selector => {
          if (selector.open && !selector.contains(event.target)) selector.open = false;
        });
      });

      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        selectors.forEach(selector => {
          if (!selector.open) return;
          selector.open = false;
          selector.querySelector('summary')?.focus();
        });
      });
    }
  };

  /* ====================================================================
     SEO & Analytics Helpers
     ==================================================================== */

  const AnalyticsHelper = {
    init() {
      // Track search queries
      const searchForm = document.querySelector('.header__search-form');
      searchForm?.addEventListener('submit', function () {
        const query = this.querySelector('input[name="q"]')?.value;
        if (query && typeof gtag !== 'undefined') {
          gtag('event', 'search', { search_term: query });
        }
      });
    }
  };

  const HeaderNavigation = {
    init() {
      const toggles = Array.from(document.querySelectorAll('.header__dropdown-toggle'));
      if (!toggles.length) return;

      const closeAll = (except) => {
        toggles.forEach(toggle => {
          if (toggle === except) return;
          toggle.setAttribute('aria-expanded', 'false');
          document.getElementById(toggle.getAttribute('aria-controls'))?.classList.remove('header__dropdown--open');
        });
      };

      toggles.forEach(toggle => {
        toggle.addEventListener('click', event => {
          event.stopPropagation();
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          closeAll(toggle);
          toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          document.getElementById(toggle.getAttribute('aria-controls'))?.classList.toggle('header__dropdown--open', !expanded);
        });
      });

      document.addEventListener('click', () => closeAll());
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const activeToggle = toggles.find(toggle => toggle.getAttribute('aria-expanded') === 'true');
        if (!activeToggle) return;
        closeAll();
        activeToggle.focus();
      });
    }
  };

  /* ====================================================================
     Boot
     ==================================================================== */

  document.addEventListener('DOMContentLoaded', () => {
    CartModule.init();
    CardQuickAdd.init();
    FilterModule.init();
    syncFilterSidebarMode();
    CollectionModule.init();
    ProductModule.init();
    BackToTop.init();
    SmartHeader.init();
    HeaderNavigation.init();
    AnimOnScroll.init();
    HeroPresentation.init();
    AccessibleAccordion.init();
    ContactFormPresentation.init();
    NewsletterFormPresentation.init();
    LocalizationForms.init();
    AnalyticsHelper.init();
    ZodiacDonut.init();
    ZodiacCarousel.init();
    CollectionLineup.init();
    ProductGallery.init();

    // Apply saved collection view preference
    CollectionModule.applySavedView();
  });

})();
